import { cacheTiers } from "@/api/queryClient";
import { qk } from "@/api/queryKeys";
import { useApiQuery } from "@/api/useApiQuery";
import { useUserKey } from "@/api/useUserKey";
import { getDashboardConfig } from "@/service/reqUrl.service";

import type { DashboardConfigResponse } from "./types";

interface UseDashboardConfigResult {
  data: DashboardConfigResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const EMPTY: DashboardConfigResponse = {
  customerSystemID: "",
  serviceLines: [],
  filterOptions: {},
  roles: [],
  dashboards: [],
  dataRefreshDate: null,
};

/**
 * The insight UI's bootstrap call. Fires once when the dashboard shell mounts —
 * i.e. immediately after login navigates here, and again on a page refresh —
 * and returns everything the shell needs before it can render: the user's
 * service lines, every filter's dropdown options, the roles they may view as,
 * which dashboards (metric categories) each of those roles may open, and the
 * warehouse's last refresh timestamp.
 *
 * Both SP inputs (CustomerSystemID, UserEmail) are derived server-side from the
 * JWT, so there is nothing to send — and the lists that come back are already
 * scoped to this user by the stored procedure.
 *
 * CACHE (tier A, 30 min): this data changes only by admin action, so it is now
 * read once per session rather than on every mount of the store provider. That
 * alone removes the refetch that used to happen on every Old-UI → New-UI
 * navigation, since `DaylightStoreProvider` unmounts on the way out.
 */
export function useDashboardConfig(): UseDashboardConfigResult {
  const userKey = useUserKey();

  const { data, loading, error, refetch } = useApiQuery<
    DashboardConfigResponse | null,
    DashboardConfigResponse
  >({
    queryKey: qk.config(userKey),
    url: getDashboardConfig,
    body: null,
    method: "GET",
    fallbackMessage: "Failed to fetch dashboard config",
    // A successful call with an empty body still counts as "config loaded" —
    // `store.tsx` reads `config != null` to tell "the server granted this user
    // nothing" (gate closed) apart from "config hasn't arrived" (degrade open).
    select: (raw) => raw ?? EMPTY,
    // One of only two hooks that toasted on an HTTP failure before the
    // migration (`if (toast) showErrorToast(message)`). Everything else renders
    // its error inline only — see `reportError` in `api/queryClient.ts`.
    meta: { toastOnError: true },
    ...cacheTiers.bootstrap,
  });

  return { data, loading, error, refetch };
}
